import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { noop, useTheme, useMediaQuery, useWindowContext, Button } from '@iqueue/ui-kit'
import { Source, Layer } from 'react-map-gl'
import { LngLatBounds } from 'mapbox-gl'
import NodePresenter from '../../api/nodePresenter'
import GLMap from '../../components/map/GLMap'
import useTranslate, { t } from '../../hooks/useTranslate'

function MapView({ onIdle = noop, satellite = false, interactive = true, ...props }) {
  const ref = useRef()
  const { togglePaddings, setTitle, toggleScrollbars } = useWindowContext()
  const [zoomLevel, setZoomLevel] = useState(() => {
    return parseFloat(localStorage.getItem('mapZoom')) || 5
  })
  const [center, setCenter] = useState(() => {
    const savedCenter = localStorage.getItem('mapCenter')
    return savedCenter ? JSON.parse(savedCenter) : { lat: 41.3111519, lng: 69.2797104 }
  })
  const [state, setState] = useState([])
  const [cursor, setCursor] = useState('grab')
  const [mapColors, setMapColors] = useState({
    textColor: '',
    haloColor: '',
    textFont: ['Roboto Medium', 'Arial Unicode MS Regular'],
    textMaxWidth: 30,
    textLineHeight: 1.5,
    textSize: 14,
  })

  const locale = useTranslate()

  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const { theme } = useTheme()

  const mapStyle = useMemo(() => {
    return satellite
      ? 'mapbox://styles/mapbox/satellite-streets-v12'
      : theme === 'auto'
        ? prefersDarkMode
          ? 'mapbox://styles/mapbox/navigation-guidance-night-v4'
          : 'mapbox://styles/mapbox/navigation-guidance-day-v4'
        : theme === 'light'
          ? 'mapbox://styles/mapbox/navigation-guidance-day-v4'
          : 'mapbox://styles/mapbox/navigation-guidance-night-v4'
  }, [theme, satellite, prefersDarkMode])

  const initialViewState = useMemo(() => ({
    latitude: center.lat,
    longitude: center.lng,
    zoom: zoomLevel,
  }), [center, zoomLevel])

  const geojsonData = useMemo(() => {
    const items = state || []
    const features = []
    const organizationFeatures = []
    const factoryFeatures = []
    const fieldFeatures = []
    const collectionPointFeatures = []
    const wellFeatures = []
    items.forEach((node) => {
      switch (node.type) {
        case 'ORGANIZATION':
          organizationFeatures.push({
            type: 'ORGANIZATION',
            properties: {
              id: node.id,
              type: node.type,
              icon: 'corporate_fare',
              parent: node.parent,
            },
          })
          break
        case 'FACTORY':
          factoryFeatures.push({
            type: 'FACTORY',
            properties: {
              id: node.id,
              type: node.type,
              icon: 'factory',
              parent: node.parent,
            },
          })
          break
        case 'FIELD':
          if (node.location) {
            features.push({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(node.location.lng), parseFloat(node.location.lat)],
              },
              properties: {
                id: node.id,
                type: node.type,
                description: node.name,
              },
            })
          }

          if (node.geoJson?.features) {
            fieldFeatures.push(...node.geoJson?.features.map((feature) => ({
              type: 'FIELD',
              geometry: feature.geometry,
              properties: {
                id: node.id,
                type: node.type,
                icon: 'water_pump',
                level: 3,
              },
            })))
          }
          break
        case 'COLLECTION_POINT':
          collectionPointFeatures.push({
            type: 'COLLECTION_POINT',
            properties: {
              id: node.id,
              type: node.type,
              icon: 'oil_barrel',
              parent: node.parent,
            },
          })
          break
        case 'WELL':
          wellFeatures.push({
            type: 'WELL',
            properties: {
              id: node.id,
              type: node.type,
              icon: 'local_fire_department',
              parent: node.parent,
            },
          })
          break
        default:
          console.warn(`Unknown type for node ${ node.id }: ${ node.type }`)
      }

      if (node.location && node.type !== 'FIELD') {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(node.location.lng), parseFloat(node.location.lat)],
          },
          properties: {
            id: node.id,
            type: node.type,
            description: node.name,
          },
        })
      }
    })

    return {
      type: 'FeatureCollection',
      features: [
        ...features,
        ...organizationFeatures,
        ...factoryFeatures,
        ...collectionPointFeatures,
        ...wellFeatures,
      ],
      organizationFeatures,
      factoryFeatures,
      fieldFeatures,
      collectionPointFeatures,
      wellFeatures,
    }
  }, [state])

  const geojsonLinks = useMemo(() => {
    const items = state || []
    const links = []

    items.forEach(node => {
      if (node.parentId) {
        const parentNode = items.find(n => n.id === node.parentId)
        if (parentNode) {
          links.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [
                [parseFloat(parentNode.location.lng), parseFloat(parentNode.location.lat)],
                [parseFloat(node.location.lng), parseFloat(node.location.lat)],
              ],
            },
            properties: {
              fromType: parentNode.type,
              toType: node.type,
            },
          })
        }
      }
    })

    return {
      type: 'FeatureCollection',
      features: links,
    }
  }, [state])

  const handleZoomChange = useCallback((e) => {
    const newZoomLevel = e.target.getZoom()
    setZoomLevel(newZoomLevel)
    localStorage.setItem('mapZoom', JSON.stringify(newZoomLevel))

    const map = ref.current && ref.current.getMap()
    if (map) {
      const mapStyle = map.getStyle()
      const textLayer = mapStyle.layers.find(
        (layer) => layer.type === 'symbol' && layer.layout && layer.layout['text-field'],
      )

      if (textLayer) {
        setMapColors({
          textColor: textLayer.paint?.['text-color'],
          haloColor: textLayer.paint?.['text-halo-color'] || "#000",
          textFont: textLayer.layout['text-font'],
          textMaxWidth: textLayer.layout['text-max-width'],
          textLineHeight: textLayer.layout['text-line-height'],
          textSize: textLayer.layout['text-size'],
        })
      }
    }
  }, [])

  const handleMoveEnd = useCallback(() => {
    const map = ref.current && ref.current.getMap()
    if (map) {
      const newCenter = map.getCenter()
      const centerObj = { lat: newCenter.lat, lng: newCenter.lng }
      setCenter(centerObj)
      localStorage.setItem('mapCenter', JSON.stringify(centerObj))
    }
  }, [])

  const handleMapClick = useCallback((event) => {
    const map = ref.current && ref.current.getMap()
    if (!map) {
      return
    }

    const features = map.queryRenderedFeatures(event.point)

    if (features.length > 0) {
      const feature = features[0]
      if (feature && feature.properties && feature.properties.id) {
        const itemId = feature.properties.id
        window.location.href = `/production/${ itemId }`
      } else {
        console.warn('Feature does not have an id:', feature)
      }
    } else {
      console.warn('No features found at clicked location')
    }
  }, [])

  const handleMouseEnter = useCallback(() => setCursor('pointer'), [])
  const handleMouseLeave = useCallback(() => setCursor('grab'), [])
  const translatedTitle = useMemo(() => t('menu.map'), [locale])

  const fitToDevices = useCallback(() => {
    const bounds = state.reduce((bounds, { location }) => {
      const loc = [parseFloat(location.lng), parseFloat(location.lat)]
      if (!bounds) bounds = new LngLatBounds(loc, loc)
      return bounds.extend(loc)
    }, null)

    if (bounds && ref.current) {
      const map = ref.current.getMap()
      const padding = Math.min(window.outerWidth, window.outerHeight) * 0.1
      map.fitBounds(bounds, { padding })
    }
  }, [state])

  useEffect(() => {
    if (!interactive && state.length === 1 && ref.current) {
      const map = ref.current.getMap()
      if (zoomLevel) map.setZoom(zoomLevel)

      if (state[0]?.location) {
        map.setCenter({
          lat: state[0].location.lat,
          lng: state[0].location.lng,
        })
      }
    }
  }, [zoomLevel, interactive, state])

  useEffect(() => {
    setTitle('Map')
    togglePaddings(false)
    toggleScrollbars(false)
  }, [setTitle, togglePaddings, toggleScrollbars])

  useEffect(() => {
    const cleanup = NodePresenter.read().then((items) => {
      setState(items)
    })
    return () => cleanup
  }, [])

  useEffect(() => {
    const map = ref.current && ref.current?.getMap()
    if (map) {
      map.on('load', () => {
        const icons = ['corporate_fare', 'factory', 'water_pump', 'oil_barrel', 'local_fire_department']
        icons.forEach((icon) => {
          const img = new Image(24, 24)
          img.onload = () => map.addImage(icon, img)
          img.onerror = () => console.warn(`Ошибка загрузки иконки ${ icon }`)
          img.src = `/response/${ icon }.png`
        })
      })
    }
  }, [ref.current])

  useEffect(() => {
    setTitle(translatedTitle)
    togglePaddings(false)
  }, [translatedTitle])

  return <GLMap
    ref={ ref }
    initialViewState={ initialViewState }
    mapStyle={ mapStyle }
    onZoom={ handleZoomChange }
    onLoad={ handleZoomChange }
    onMoveEnd={ handleMoveEnd }
    onMouseEnter={ handleMouseEnter }
    onMouseLeave={ handleMouseLeave }
    mapboxAccessToken={ null }
    cursor={ cursor }
    onClick={ handleMapClick }
    interactiveLayerIds={ [
      'level-1-symbol',
      'level-2-symbol',
      'level-3-symbol',
      'level-4-symbol',
      'level-5-symbol',
    ] }
    { ...props }
  >
    <Source id="heatmap" type="geojson" data={ geojsonData }>
      <Layer
        id="heatmap-layer"
        type="heatmap"
        maxZoom={ 9 }
        paint={ {
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)',
          ],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 15],
          'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 0, 0, 5, 2, 10, 0],
        } }
      />
    </Source>
    <Source id="field-polygons" type="geojson" data={ {
      type: 'FeatureCollection',
      features: geojsonData.fieldFeatures,
    } }>
      <Layer
        id="field-polygon-layer"
        type="fill"
        minZoom={ 10 }
        paint={ {
          'fill-color': '#FFD700',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            12, 0.4,
          ],
          'fill-outline-color': '#FFFFFF',
        } }
      />
    </Source>
    <Source id="links" type="geojson" data={ geojsonLinks }>
      <Layer
        id="link-layer-1-2"
        type="line"
        minZoom={ 9 }
        paint={ {
          'line-color': '#1E90FF',
          'line-width': 2,
          'line-opacity': zoomLevel > 9 ? 1 : 0,
        } }
        filter={ ['all', ['==', ['get', 'fromType'], 'ORGANIZATION'], ['==', ['get', 'toType'], 'FACTORY']] }
      />
      <Layer
        id="link-layer-2-3"
        type="line"
        minZoom={ 10 }
        paint={ {
          'line-color': '#32CD32',
          'line-width': 2,
          'line-opacity': zoomLevel >= 10 ? 1 : 0,
        } }
        filter={ ['all', ['==', ['get', 'fromType'], 'FACTORY'], ['==', ['get', 'toType'], 'FIELD']] }
      />
      <Layer
        id="link-layer-3-4"
        type="line"
        minZoom={ 10 }
        paint={ {
          'line-color': 'rgba(196,9,9,0.19)',
          'line-width': 2,
          'line-opacity': zoomLevel >= 10 ? 1 : 0,
        } }
        filter={ ['all', ['==', ['get', 'fromType'], 'FIELD'], ['==', ['get', 'toType'], 'COLLECTION_POINT']] }
      />
      <Layer
        id="link-layer-4-5"
        type="line"
        minZoom={ 10 }
        paint={ {
          'line-color': '#FFD700',
          'line-width': 2,
          'line-opacity': zoomLevel >= 10 ? 1 : 0,
        } }
        filter={ ['all', ['==', ['get', 'fromType'], 'COLLECTION_POINT'], ['==', ['get', 'toType'], 'WELL']] }
      />
    </Source>

    <Source id="nodes" type="geojson" data={ geojsonData }>
      <Layer
        id="level-1-background"
        type="circle"
        paint={ {
          'circle-color': '#4285F4',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 18,
          ],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 2.4,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
        } }
        filter={ ['in', 'type', 'ORGANIZATION'] }
      />

      <Layer
        id="level-1-symbol"
        type="symbol"
        layout={ {
          'icon-image': 'corporate_fare',
          'icon-size': 1,
          'icon-anchor': 'center',
          'text-field': ['get', 'description'],
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-size': 14,
          'text-font': ['Roboto Medium', 'Arial Unicode MS Regular'],
          'text-justify': 'center',
          'text-max-width': 30,
          'text-transform': 'none',
          'text-allow-overlap': true,
          'text-line-height': 1.5,
        } }
        paint={ {
          'text-color': mapColors.textColor,
          'text-halo-color': mapColors.haloColor,
          'text-halo-width': 1.5,
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            1, 1,
          ],
        } }
        filter={ ['in', 'type', 'ORGANIZATION'] }
      />

      <Layer
        id="level-2-background"
        type="circle"
        paint={ {
          'circle-color': '#32CD32',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 17,
          ],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 2.4,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
        } }
        filter={ ['in', 'type', 'FACTORY'] }
      />

      <Layer
        id="level-2-symbol"
        type="symbol"
        layout={ {
          'icon-image': 'factory',
          'icon-size': 0.9,
          'icon-anchor': 'center',
          'text-field': ['get', 'description'],
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-size': 14,
          'text-font': ['Roboto Medium', 'Arial Unicode MS Regular'],
          'text-justify': 'center',
          'text-max-width': 30,
          'text-transform': 'none',
          'text-allow-overlap': true,
          'text-line-height': 1.5,
        } }
        paint={ {
          'text-color': mapColors.textColor,
          'text-halo-color': mapColors.haloColor,
          'text-halo-width': 1.5,
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
        } }
        filter={ ['in', 'type', 'FACTORY'] }
      />

      <Layer
        id="level-3-background"
        type="circle"
        paint={ {
          'circle-color': '#FF6200',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0,
            11, 16,
          ],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0,
            11, 2.4,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            10, 1,
          ],
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0,
            11, 1,
          ],
        } }
        filter={ ['in', 'type', 'FIELD'] }
      />

      <Layer
        id="level-3-symbol"
        type="symbol"
        layout={ {
          'icon-image': 'water_pump',
          'icon-size': 0.9,
          'icon-anchor': 'center',
          'text-field': ['get', 'description'],
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-size': 14,
          'text-font': ['Roboto Medium', 'Arial Unicode MS Regular'],
          'text-justify': 'center',
          'text-max-width': 30,
          'text-transform': 'none',
          'text-allow-overlap': true,
          'text-line-height': 1.5,
        } }
        paint={ {
          'text-color': mapColors.textColor,
          'text-halo-color': mapColors.haloColor,
          'text-halo-width': 1.5,
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0,
            11, 1,
          ],
        } }
        filter={ ['in', 'type', 'FIELD'] }
      />

      <Layer
        id="level-4-background"
        type="circle"
        paint={ {
          'circle-color': '#A90637',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            11, 0,
            12, 15,
          ],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            11, 0,
            12, 2,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 0,
            11, 1,
          ],
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            11, 0,
            12, 1,
          ],
        } }
        filter={ ['in', 'type', 'COLLECTION_POINT'] }
      />

      <Layer
        id="level-4-symbol"
        type="symbol"
        layout={ {
          'icon-image': 'oil_barrel',
          'icon-size': 0.7,
          'icon-anchor': 'center',
          'text-field': ['get', 'description'],
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-size': 14,
          'text-font': ['Roboto Medium', 'Arial Unicode MS Regular'],
          'text-justify': 'center',
          'text-max-width': 30,
          'text-transform': 'none',
          'text-allow-overlap': true,
          'text-line-height': 1.5,
        } }
        paint={ {
          'text-color': mapColors.textColor,
          'text-halo-color': mapColors.haloColor,
          'text-halo-width': 1.5,
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            11, 0,
            12, 1,
          ],
        } }
        filter={ ['in', 'type', 'COLLECTION_POINT'] }
      />

      <Layer
        id="level-5-background"
        type="circle"
        paint={ {
          'circle-color': '#8C00FF',
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0,
            13, 14,
          ],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0,
            13, 2,
          ],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            11, 0,
            12, 1,
          ],
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0,
            13, 1,
          ],
        } }
        filter={ ['in', 'type', 'WELL'] }
      />

      <Layer
        id="level-5-symbol"
        type="symbol"
        layout={ {
          'icon-image': 'local_fire_department',
          'icon-size': 0.8,
          'icon-anchor': 'center',
          'text-field': ['get', 'description'],
          'text-anchor': 'top',
          'text-offset': [0, 1],
          'text-size': 14,
          'text-font': ['Roboto Medium', 'Arial Unicode MS Regular'],
          'text-justify': 'center',
          'text-max-width': 30,
          'text-transform': 'none',
          'text-allow-overlap': true,
          'text-line-height': 1.5,
        } }
        paint={ {
          'text-color': mapColors.textColor,
          'text-halo-color': mapColors.haloColor,
          'text-halo-width': 1.5,
          'icon-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0,
            13, 1,
          ],
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            12, 0,
            13, 1,
          ],
        } }
        filter={ ['in', 'type', 'WELL'] }
      />
    </Source>

    { interactive && <div className={ 'controls bottom-right' }>
      <Button
        focusable={ false }
        icon={ 'fit_screen' }
        onClick={ fitToDevices }
      />
    </div> }
  </GLMap>
}

// export default forwardRef(MapView)
export default MapView
